import type { FocusAjaxError, Duck } from "@focus/interfaces";
import { ItemStatus } from "@focus/interfaces/lib/common/itemFetchStatus";
import { getErrorMessage } from "@focus/services/lib/helpers/errorParser";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import {
  mergeMap,
  map,
  catchError,
  takeUntil,
  startWith,
  filter,
  take,
} from "rxjs/operators";

export interface ConfigDetail {
  id: string;
  urlContext: string;
}

export interface DetailState<T = any> {
  content?: T;
  status: ItemStatus;
  errorMessage: string;
}

export function createDetailFetcher<T = any>(element: string): Duck {
  function computeURL(config: ConfigDetail): string {
    const { id, urlContext } = config;
    return `/api/v1/${urlContext}/${id}`;
  }

  // define constants
  const SET = `${element.toUpperCase()}/SET`;
  const FETCH = `${element.toUpperCase()}/FETCH_START`;
  const FETCH_STOP = `${element.toUpperCase()}/FETCH_STOP`;
  const FETCH_ERROR = `${element.toUpperCase()}/FETCH_ERROR`;
  const FETCH_PENDING = `${element.toUpperCase()}/FETCH_PENDING`;

  // ACTIONS
  const fetch = (payload: ConfigDetail) => ({
    type: FETCH,
    payload,
  });
  type FetchAction = ReturnType<typeof fetch>;

  const stopFetching = (payload: ConfigDetail) => ({
    type: FETCH_STOP,
    payload,
  });
  type StopFetchingAction = ReturnType<typeof stopFetching>;

  const setFetchError = (id: string, payload: string) => ({
    type: FETCH_ERROR,
    id,
    payload,
  });
  type SetFetchErrorAction = ReturnType<typeof setFetchError>;

  const fetchPending = (id: string) => ({
    type: FETCH_PENDING,
    id,
  });
  type FetchPendingAction = ReturnType<typeof fetchPending>;

  const set = (id: string, payload: string) => ({
    type: SET,
    id,
    payload,
  });
  type SetAction = ReturnType<typeof set>;

  type Actions =
    | FetchAction
    | StopFetchingAction
    | SetFetchErrorAction
    | FetchPendingAction
    | SetAction;

  // REDUCER
  /**
   * Default state
   */
  const initialState: DetailState<T> = {
    content: undefined,
    status: ItemStatus.PENDING,
    errorMessage: "",
  };
  /**
   * Reducer to handle the individual items
   *
   * @param  {Object} state={}
   * @param  {Object} action
   */
  function reducer(state: DetailState<T> = initialState, action: Actions) {
    switch (action.type) {
      case SET:
        return {
          ...state,
          content: action.payload,
          status: ItemStatus.READY,
          errorMessage: "",
        };
      case FETCH_ERROR:
        return {
          ...state,
          content: undefined,
          status: ItemStatus.ERROR,
          errorMessage: action.payload,
        };
      case FETCH_PENDING:
        return {
          ...state,
          content: undefined,
          status: ItemStatus.PENDING,
          errorMessage: "",
        };
      default:
        return state;
    }
  }

  // SELECTORS
  function getContent(state: DetailState<T>): T | undefined {
    return state.content;
  }

  function getStatus(state: DetailState<T>): ItemStatus {
    return state.status;
  }

  function getError(state: DetailState<T>): string {
    return state.errorMessage;
  }

  // MIDDLEWARE
  /**
   * Epic creator for the fetch
   *
   * @param  {} action$ Actions stream
   * @param  {} _ the store , not used
   * @param  {} {ajax} The ajax service
   */
  const fetchEpic = (action$, _state$, { ajax }) =>
    action$.pipe(
      ofType(FETCH),
      mergeMap((action: FetchAction) =>
        ajax.getJSON(computeURL(action.payload)).pipe(
          map((response) => set(action.payload.id, response)),
          catchError((error: FocusAjaxError) =>
            of(setFetchError(action.payload.id, getErrorMessage(error)))
          ),
          takeUntil(
            action$.pipe(
              filter(
                (stopAction): stopAction is StopFetchingAction =>
                  stopAction.type === FETCH_STOP &&
                  stopAction.payload.id === action.payload.id
              ),
              take(1)
            )
          ),
          startWith(fetchPending(action.payload.id))
        )
      )
    );

  const actionTypes = {
    SET,
    FETCH,
    FETCH_STOP,
    FETCH_ERROR,
    FETCH_PENDING,
  };
  const actions = {
    fetch,
    stopFetching,
    setFetchError,
    set,
    fetchPending,
  };
  const selectors = {
    getContent,
    getStatus,
    getError,
  };
  const middleware = [fetchEpic];
  return {
    actions,
    reducer,
    middleware,
    selectors,
    actionTypes,
  };
}
